import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export type StaffProfile = {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  store_id: string | null;
  role: "staff" | "admin";
  active: boolean;
};

export type MemberProfile = {
  id: string;
  user_id: string | null;
  member_number: string;
  first_name: string;
  last_name: string;
  email: string;
  member_since: string;
  status: string;
  store_id: string | null;
};

type AuthCtx = {
  session: Session | null;
  user: User | null;
  staff: StaffProfile | null;
  member: MemberProfile | null;
  loading: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
};

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [staff, setStaff] = useState<StaffProfile | null>(null);
  const [member, setMember] = useState<MemberProfile | null>(null);
  const [loading, setLoading] = useState(true);

  async function loadProfiles(uid: string | undefined) {
    if (!uid) { setStaff(null); setMember(null); return; }
    const [{ data: s }, { data: m }] = await Promise.all([
      supabase.from("staff" as any).select("*").eq("user_id", uid).maybeSingle(),
      supabase.from("members").select("*").eq("user_id", uid).maybeSingle(),
    ]);
    setStaff((s as any) ?? null);
    setMember((m as any) ?? null);
  }

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      // Defer profile fetch to avoid deadlock inside listener
      setTimeout(() => { loadProfiles(sess?.user?.id); }, 0);
    });

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      loadProfiles(data.session?.user?.id).finally(() => setLoading(false));
    });

    return () => { sub.subscription.unsubscribe(); };
  }, []);

  async function refresh() {
    await loadProfiles(session?.user?.id);
  }

  async function signOut() {
    await supabase.auth.signOut();
    setStaff(null);
    setMember(null);
  }

  return (
    <Ctx.Provider value={{ session, user: session?.user ?? null, staff, member, loading, refresh, signOut }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
